// consent-manager.js - Shared consent logic for TEMPROM
// Google Consent Mode v2 implementation

const CONSENT_KEY = 'temprom_consent_preferences';
const CONSENT_COOKIE = 'temprom_consent';

const ConsentManager = {
  // Initialize Google Consent Mode v2 with defaults (MUST run before gtag loads)
  initializeConsent() {
    window.dataLayer = window.dataLayer || [];
    function gtag() { dataLayer.push(arguments); }

    // Set default consent to 'denied' (GDPR compliant)
    gtag('consent', 'default', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied',
      'functionality_storage': 'denied',
      'personalization_storage': 'denied',
      'security_storage': 'granted', // Always granted for security
      'wait_for_update': 500 // Wait 500ms for consent update
    });

    // Apply saved preferences if they exist
    const saved = this.getConsent();
    if (saved) {
      this.updateConsent(saved);
    }
  },

  // Get saved consent preferences
  getConsent() {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('ConsentManager: failed to read consent from localStorage', error);
    }

    // Fallback to cookie if localStorage is unavailable
    try {
      const match = document.cookie.match(new RegExp(`${CONSENT_COOKIE}=([^;]+)`));
      if (match) {
        const analyticsGranted = match[1] === '1';
        return {
          analytics: analyticsGranted,
          functional: analyticsGranted
        };
      }
    } catch (error) {
      console.warn('ConsentManager: failed to read consent cookie', error);
    }

    return null;
  },

  // Check if user has made a consent choice
  hasConsent() {
    return this.getConsent() !== null;
  },

  // Update consent (user accepts/rejects)
  updateConsent(preferences) {
    window.dataLayer = window.dataLayer || [];
    function gtag() { dataLayer.push(arguments); }

    const analyticsGranted = !!preferences.analytics;
    const personalizationGranted = !!preferences.functional;

    // Update Google Consent Mode
    gtag('consent', 'update', {
      'ad_storage': analyticsGranted ? 'granted' : 'denied',
      'ad_user_data': analyticsGranted ? 'granted' : 'denied',
      'ad_personalization': analyticsGranted ? 'granted' : 'denied',
      'analytics_storage': analyticsGranted ? 'granted' : 'denied',
      'functionality_storage': personalizationGranted ? 'granted' : 'denied',
      'personalization_storage': personalizationGranted ? 'granted' : 'denied',
      'security_storage': 'granted'
    });

    const persistedPreferences = { analytics: analyticsGranted, functional: personalizationGranted };

    // Store preferences
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(persistedPreferences));
    } catch (error) {
      console.warn('ConsentManager: failed to persist consent in localStorage', error);
    }

    // Set consent cookie (for server-side detection if needed)
    try {
      const expires = new Date();
      expires.setFullYear(expires.getFullYear() + 1);
      document.cookie = `${CONSENT_COOKIE}=${analyticsGranted ? '1' : '0'}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    } catch (error) {
      console.warn('ConsentManager: failed to persist consent cookie', error);
    }
  },

  // Accept all
  acceptAll() {
    this.updateConsent({ analytics: true, functional: true });
  },

  // Reject all (except necessary)
  rejectAll() {
    this.updateConsent({ analytics: false, functional: false });
  }
};

// Expose globally for use in HTML scripts
window.ConsentManager = ConsentManager;

// Auto-initialize on load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    ConsentManager.initializeConsent();
  });
} else {
  // DOMContentLoaded already fired
  ConsentManager.initializeConsent();
}
